"use client";

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { claimSignupBonus } from "@/lib/tokens";
import {
  addGuestTokens as addGuestTokensLocal,
  clearGuestTokens,
  getGuestTokens,
} from "@/lib/guestTokens";

export interface Profile {
  id: string;
  username: string;
  tokens: number;
  last_roll_at: string | null;
  last_daily_claim: string | null;
  intro_seen: boolean;
  // Escalating daily-login streak day (1..7), and the one-time sign-in bonus
  // guard. Both server-managed.
  daily_streak: number;
  signup_bonus_claimed: boolean;
  // Server-set dev/admin flag (see lib/admin.ts). Clients can't write it.
  is_admin: boolean;
  created_at: string;
  // Cosmetic flair (vanity only). Owned cosmetic ids + the equipped title/color.
  // Optional for backward-compat before the cosmetics migration has run; written
  // only via the purchase_cosmetic / equip_cosmetic RPCs.
  owned_cosmetics?: string[];
  equipped_title?: string | null;
  equipped_color?: string | null;
}

interface AuthContextValue {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  refreshProfile: () => Promise<void> | void;
  signOut: () => Promise<unknown>;
  // Replaces the cached token balance in place so every consumer (header,
  // results screen, /tokens) reflects an earn/spend instantly without a
  // round-trip. The authoritative value always comes from a token RPC.
  applyTokenBalance: (balance: number) => void;
  // The not-signed-in player's token balance (localStorage-backed), or null
  // until hydrated. Signed-in players use profile.tokens instead.
  guestTokens: number | null;
  // Adds tokens to the guest balance (e.g. the share bonus before sign-in).
  addGuestTokens: (amount: number) => void;
  // A short-lived toast message after a token grant (daily / sign-in bonus),
  // or null. The provider renders it.
  tokenToast: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [guestTokens, setGuestTokens] = useState<number | null>(null);
  const [tokenToast, setTokenToast] = useState<string | null>(null);
  // Ensures the per-session token grants (sign-in + daily bonus) fire once per
  // signed-in session (the 24h / one-time gates are enforced server-side too).
  const tokenGrantsCheckedFor = useRef<string | null>(null);

  // Hydrate the guest token balance once after mount (post-mount so the first
  // client render matches the server-rendered HTML).
  useEffect(() => {
    setGuestTokens(getGuestTokens());
  }, []);

  const addGuestTokens = useCallback((amount: number) => {
    setGuestTokens(addGuestTokensLocal(amount));
  }, []);

  const loadProfile = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();
    setProfile(data ?? null);
  }, []);

  // Loads the profile row for a session, creating it on first login if it
  // doesn't exist yet (OAuth sign-ins, or email users confirming for the
  // first time). Default username comes from signup metadata or the OAuth
  // provider's profile fields.
  const ensureProfile = useCallback(async (sessionUser: User) => {
    const { data: existing } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", sessionUser.id)
      .single();

    if (existing) {
      setProfile(existing);
      return;
    }

    const meta = sessionUser.user_metadata ?? {};
    const baseUsername = (
      meta.username ||
      meta.full_name ||
      meta.name ||
      meta.user_name ||
      sessionUser.email?.split("@")[0] ||
      "Player"
    )
      .toString()
      .trim()
      .slice(0, 24);

    let username = baseUsername || "Player";
    for (let attempt = 0; attempt < 5; attempt++) {
      const { data: created, error } = await supabase
        .from("profiles")
        .insert({ id: sessionUser.id, username })
        .select()
        .single();

      if (!error) {
        setProfile(created);
        return;
      }

      if (error.code === "23505") {
        username = `${baseUsername.slice(0, 18)}-${sessionUser.id.slice(0, 4)}`;
        continue;
      }

      // RLS failure or other error (e.g. no session yet during pending
      // email confirmation) - leave profile unset quietly.
      setProfile(null);
      return;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      const sessionUser = data.session?.user ?? null;
      setUser(sessionUser);
      if (sessionUser) {
        ensureProfile(sessionUser).finally(() => {
          if (!cancelled) setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        const sessionUser = session?.user ?? null;
        setUser(sessionUser);
        if (sessionUser) {
          ensureProfile(sessionUser);
        } else {
          setProfile(null);
          tokenGrantsCheckedFor.current = null;
          // Back to guest mode — re-read the localStorage balance.
          setGuestTokens(getGuestTokens());
        }
      },
    );

    return () => {
      cancelled = true;
      listener.subscription.unsubscribe();
    };
  }, [ensureProfile]);

  const applyTokenBalance = useCallback((balance: number) => {
    setProfile((prev) => (prev ? { ...prev, tokens: balance } : prev));
  }, []);

  // One-time sign-in bonus (+25 + migrated guest balance), once a signed-in
  // profile is available. The escalating daily login reward is NOT claimed here
  // — it's claimed from the DailyRewardPopup so the player sees it and taps to
  // collect. The server enforces the one-time gate regardless.
  useEffect(() => {
    if (!user || !profile) return;
    if (tokenGrantsCheckedFor.current === user.id) return;
    tokenGrantsCheckedFor.current = user.id;
    if (profile.signup_bonus_claimed) {
      // Nothing to migrate; just make sure no stale guest balance lingers.
      clearGuestTokens();
      setGuestTokens(0);
      return;
    }
    (async () => {
      try {
        const carry = getGuestTokens();
        const res = await claimSignupBonus(carry);
        if (res.claimed) {
          applyTokenBalance(res.balance);
          setTokenToast(`+${res.granted} welcome tokens`);
        }
      } catch {
        /* leave the guest balance in place to retry next session */
      }
      clearGuestTokens();
      setGuestTokens(0);
    })();
  }, [user, profile, applyTokenBalance]);

  useEffect(() => {
    if (!tokenToast) return;
    const t = setTimeout(() => setTokenToast(null), 4000);
    return () => clearTimeout(t);
  }, [tokenToast]);

  const refreshProfile = useCallback(() => {
    if (user) return loadProfile(user.id);
  }, [user, loadProfile]);

  const signOut = useCallback(() => supabase.auth.signOut(), []);

  const value: AuthContextValue = {
    user,
    profile,
    loading,
    refreshProfile,
    signOut,
    applyTokenBalance,
    guestTokens,
    addGuestTokens,
    tokenToast,
  };

  return createElement(
    AuthContext.Provider,
    { value },
    children,
    tokenToast
      ? createElement(
          "div",
          {
            key: "token-toast",
            className:
              "animate-slide-down fixed left-1/2 top-4 z-[60] -translate-x-1/2 rounded-full border border-accent/40 bg-background/90 px-5 py-2 text-xs font-bold uppercase tracking-widest text-accent shadow-[0_0_30px_rgba(124,58,237,0.4)] backdrop-blur",
          },
          `🪙 ${tokenToast}`,
        )
      : null,
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
